import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { AdminTitle } from "@/admin/AdminLayout";

export default function AdminArticles() {
  const { sections, refresh } = useSite();
  const [section, setSection] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const secName = Object.fromEntries(sections.map((s) => [s.id, s.name]));

  const load = () => api.get("/admin/articles", { params: { section: section || undefined, q: q || undefined, page, limit: 50 } }).then((r) => setData(r.data));
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [section, q, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try { await api.delete(`/articles/${a.id}`); toast.success("Deleted"); load(); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-articles">
      <AdminTitle eyebrow="The pages of the codex" title="Articles">
        <Link to={`/admin/articles/new${section ? `?section=${sections.find((s) => s.id === section)?.slug}` : ""}`} data-testid="admin-new-article" className="btn-illum theme-flame"><Plus className="w-4 h-4" /> New article</Link>
      </AdminTitle>
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2 admin-input !w-72"><Search className="w-4 h-4" /><input data-testid="admin-articles-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search titles…" className="bg-transparent outline-none w-full" /></div>
        <select data-testid="admin-articles-section-filter" value={section} onChange={(e) => { setSection(e.target.value); setPage(1); }} className="admin-input !w-64">
          <option value="">All sections</option>
          {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {data && <span className="self-center text-sm italic text-[#5e4a35]">{data.total} pages</span>}
      </div>
      <div className="vellum overflow-x-auto">
        <table className="w-full text-left">
          <thead className="font-heading text-[11px] uppercase tracking-[0.16em] text-[#5e4a35] border-b border-[#c9a227]/50">
            <tr><th className="p-4">Title</th><th className="p-4">Section</th><th className="p-4">Status</th><th className="p-4">Updated</th><th className="p-4 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {data?.items.map((a) => (
              <tr key={a.id} data-testid={`admin-article-row-${a.slug}`} className="border-b border-[#c9a227]/20 hover:bg-[#c9a227]/10">
                <td className="p-4 font-heading font-semibold"><Link to={`/admin/articles/${a.id}`} className="hover:text-[#991b1b]">{a.title}</Link></td>
                <td className="p-4 text-sm">{secName[a.section_id] || "—"}</td>
                <td className="p-4 text-xs font-heading">{a.published ? <span className="text-[#2f6b3a]">Published</span> : <span className="text-[#991b1b]">Draft</span>}</td>
                <td className="p-4 text-sm text-[#5e4a35]">{new Date(a.updated_at).toLocaleDateString()}</td>
                <td className="p-4">
                  <div className="flex justify-end gap-1">
                    {a.published && <Link to={`/wiki/${a.slug}`} target="_blank" className="p-2 hover:bg-[#c9a227]/20"><Eye className="w-4 h-4" /></Link>}
                    <Link to={`/admin/articles/${a.id}`} data-testid={`admin-edit-${a.slug}`} className="p-2 hover:bg-[#c9a227]/20"><Pencil className="w-4 h-4" /></Link>
                    <button onClick={() => remove(a)} data-testid={`admin-delete-${a.slug}`} className="p-2 text-[#991b1b] hover:bg-[#991b1b]/10"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.items.length === 0 && <p className="p-8 italic text-center">No articles found.</p>}
      </div>
      {data?.pages > 1 && (
        <div className="flex gap-2 mt-6">{Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
          <button key={p} onClick={() => setPage(p)} className={`w-9 h-9 border border-[#c9a227] font-heading ${p === page ? "bg-[#1e4976] text-white" : ""}`}>{p}</button>
        ))}</div>
      )}
    </div>
  );
}
